import {Injectable, NgZone, OnDestroy, computed, inject, signal} from '@angular/core';
import {FirebaseApi} from '../../common/services/firebase-api.service';
import {Logger} from '../../common/utils/logger';

/** A file's way through an import, as the server records it (functions' import-jobs.ts). */
export type ImportJobStatus = 'waiting' | 'uploading' | 'queued' | 'reading' | 'saving' | 'done' | 'failed';

/** One file of an import, as the progress list shows it. */
export interface ImportFileView {
  id: string;
  fileName: string;
  /** `interrupted`: an upload whose page has gone quiet (closed or offline). */
  status: ImportJobStatus | 'interrupted';
  /** While uploading or saving. */
  percent: number | null;
  items: number | null;
  error: string | null;
}

/** One import (a batch of files), as the progress chip and its list show it. */
export interface ImportBatchView {
  id: string;
  mode: 'add' | 'replace';
  /** Started by whoever is signed in here (from this page or another). */
  mine: boolean;
  /** Started from this page: its uploads are known first-hand, and its message is its own. */
  here: boolean;
  /** Who started it; empty for an anonymous visitor. */
  name: string;
  /**
   * `finishing`: every file has ended, or its upload stopped, and the server
   * is still to finish the import (a Replace's clear included).
   */
  status: 'running' | 'finishing' | 'clearing' | 'done';
  files: ImportFileView[];
  finished: number;
  result: {items: number, failed: number, cleared: boolean, notCleared: boolean} | null;
  finishedOn: number | null;
}

/** A batch not heard of for this long has stopped uploading: its page reports every 15 s. */
const UPLOAD_QUIET_MS = 60 * 1000;
/** How long one's own finished import stays in the list, unless dismissed. */
const FINISHED_SHOWN_MS = 10 * 60 * 1000;
const LISTENED_BATCHES = 10;
const LISTENED_JOBS = 200;

/**
 * The imports of the open project, for everyone on it: a project's imports
 * run on the server (POST /import/:id/batches) and are recorded under it
 * (importBatches, importJobs), so a reload, or another person, sees them as
 * they go. ProjectService adds what only the importing page knows: how far
 * its uploads are, and which imports it started.
 */
@Injectable({
  providedIn: 'root'
})
export class ImportJobsService implements OnDestroy {
  private firebaseApi = inject(FirebaseApi);
  private zone = inject(NgZone);

  private projectId: string | null = null;
  private stopListening: (() => void)[] = [];
  private readonly batchDocs = signal<any[]>([]);
  private readonly jobDocs = signal<any[]>([]);
  /** Upload progress this page knows first-hand, by job. */
  private readonly uploads = signal<{[jobId: string]: number}>({});
  /** Imports started from this page. */
  private readonly own = signal<Set<string>>(new Set());
  private readonly dismissed = signal<Set<string>>(new Set());
  /**
   * When this page last saw each batch's heartbeat change, by its own clock:
   * whether an upload has gone quiet is judged by it, not by the server's
   * time against this computer's (a clock a minute off called live uploads
   * stopped).
   */
  private readonly heard = new Map<string, {heartbeatOn: number, at: number}>();
  /** Ticks while anything is shown: an upload going quiet, a finished import leaving the list. */
  private readonly now = signal(Date.now());
  private clock: any = null;

  /** The imports to show: those running, and one's own finished ones for a while. */
  readonly batches = computed<ImportBatchView[]>(() => {
    const now = this.now();
    const uid = this.firebaseApi.getCurrentUser()?.uid;
    const jobs = this.jobDocs();
    const uploads = this.uploads();
    const own = this.own();
    const dismissed = this.dismissed();
    return this.batchDocs()
      .filter(batch => !dismissed.has(batch.id))
      .filter(batch => batch.status !== 'done'
        || (batch.uid === uid && now - (batch.finishedOn || 0) < FINISHED_SHOWN_MS))
      .map(batch => {
        const here = own.has(batch.id);
        const heard = this.heard.get(batch.id);
        const quiet = !here && !!heard && now - heard.at > UPLOAD_QUIET_MS;
        const files = jobs.filter(job => job.batchId === batch.id)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(job => this.fileView(job, here, quiet, uploads[job.id]));
        // Every file ended, or its upload stopped: the server finishes the
        // import at its next check (watchImports), a Replace's clear with it.
        const settled = files.every(file => ['done', 'failed', 'interrupted'].includes(file.status));
        const status: ImportBatchView['status'] = batch.status === 'done' ? 'done'
          : batch.status === 'clearing' ? 'clearing'
          : batch.status === 'finishing' || settled ? 'finishing' : 'running';
        return {
          id: batch.id,
          mode: batch.mode === 'replace' ? 'replace' : 'add',
          mine: here || (!!uid && batch.uid === uid),
          here,
          name: batch.name || '',
          status,
          files,
          finished: files.filter(file => file.status === 'done' || file.status === 'failed').length,
          result: batch.result || null,
          finishedOn: batch.finishedOn || null,
        } as ImportBatchView;
      })
      .filter(batch => batch.files.length > 0);
  });

  /** Whether an import is running on the project: someone else's included. */
  readonly running = computed(() => this.batches().some(batch => batch.status !== 'done'));

  private fileView(job: any, here: boolean, quiet: boolean, uploaded: number | undefined): ImportFileView {
    let status: ImportFileView['status'] = job.status;
    let percent: number | null = null;
    if (job.status === 'waiting' || job.status === 'uploading') {
      if (quiet) {
        // The page uploading it has gone: closed, reloaded or offline.
        status = 'interrupted';
      } else if (job.status === 'uploading') {
        const size = Math.max(1, job.size || 0);
        percent = Math.round((here && uploaded !== undefined ? uploaded : job.uploaded || 0) / size * 100);
      }
    } else if (job.status === 'saving' && job.progress && job.progress.total) {
      percent = Math.round(job.progress.saved / job.progress.total * 100);
    }
    return {
      id: job.id,
      fileName: job.fileName || '',
      status,
      percent: percent === null ? null : Math.max(0, Math.min(100, percent)),
      items: job.status === 'done' && job.result ? job.result.items || 0 : null,
      error: job.status === 'failed' ? job.error || 'import_failed' : null,
    };
  }

  /** Follows the imports of the project opened (none for the demo, or when none is open). */
  watch(projectId: string | null) {
    if (projectId === this.projectId) {
      return;
    }
    this.stop();
    this.projectId = projectId;
    if (!projectId) {
      return;
    }
    const project = this.firebaseApi.getProjectRef(projectId).ref;
    const failed = (what: string) => (e: any) => {
      Logger.error(`Import progress: ${what} not followed`, e);
      // Ended by Firestore: followed again on the next watch of this project.
      if (this.projectId === projectId) {
        this.zone.run(() => this.stop());
      }
    };
    this.stopListening.push(
      project.collection('importBatches').orderBy('createdOn', 'desc').limit(LISTENED_BATCHES)
        .onSnapshot(snap => this.zone.run(() => {
          const at = Date.now();
          const batches = snap.docs.map(d => d.data());
          batches.forEach(batch => {
            const known = this.heard.get(batch.id);
            const heartbeatOn = batch.heartbeatOn || batch.createdOn || 0;
            if (!known || known.heartbeatOn !== heartbeatOn) {
              this.heard.set(batch.id, {heartbeatOn, at});
            }
          });
          this.batchDocs.set(batches);
          this.tick();
        }), failed('imports')),
      project.collection('importJobs').orderBy('createdOn', 'desc').limit(LISTENED_JOBS)
        .onSnapshot(snap => this.zone.run(() => this.jobDocs.set(snap.docs.map(d => d.data()))), failed('files')),
    );
  }

  /** An import this page started: its uploads are reported here (uploadProgress) and its message is its own. */
  adopt(batchId: string) {
    this.own.update(set => new Set(set).add(batchId));
  }

  uploadProgress(jobId: string, uploaded: number) {
    this.uploads.update(map => ({...map, [jobId]: uploaded}));
  }

  dismiss(batchId: string) {
    this.dismissed.update(set => new Set(set).add(batchId));
  }

  /** Whether this page is still uploading files of an import: leaving it would stop them. */
  hasUploadsHere(): boolean {
    return this.batches().some(batch => batch.here
      && batch.files.some(file => file.status === 'waiting' || file.status === 'uploading'));
  }

  /**
   * The import's batch once it is done, followed on its own: the page that
   * started it may have opened another project meanwhile. Fails if the batch
   * is gone (the project deleted).
   */
  whenFinished(projectId: string, batchId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const ref = this.firebaseApi.getProjectRef(projectId).ref.collection('importBatches').doc(batchId);
      const stop = ref.onSnapshot(snap => {
        const batch = snap.data();
        if (!snap.exists || (batch && batch.status === 'done')) {
          stop();
          this.zone.run(() => snap.exists ? resolve(batch) : reject(new Error('import_gone')));
        }
      }, e => {
        stop();
        this.zone.run(() => reject(e));
      });
    });
  }

  /** The import's files as recorded, and the ids of the shapes each added (kept apart, as they can be many). */
  async filesOf(projectId: string, batchId: string): Promise<{job: any, geometries: string[]}[]> {
    const jobs = this.firebaseApi.getProjectRef(projectId).ref.collection('importJobs');
    const snap = await jobs.where('batchId', '==', batchId).get();
    return Promise.all(snap.docs.map(async doc => {
      const job = doc.data();
      let geometries: string[] = [];
      if (job.status === 'done' && job.result && job.result.geometries > 0) {
        const parts = await doc.ref.collection('importCreated').get();
        geometries = parts.docs.sort((a, b) => Number(a.id) - Number(b.id)).flatMap(part => part.data().ids || []);
      }
      return {job, geometries};
    })).then(list => list.sort((a, b) => (a.job.order || 0) - (b.job.order || 0)));
  }

  /** What a Replace's clear removed, as the server recorded it. */
  async clearedOf(projectId: string, batchId: string): Promise<{geometries: string[], groups: string[], layers: string[]}> {
    const parts = await this.firebaseApi.getProjectRef(projectId).ref.collection('importBatches').doc(batchId)
      .collection('importCleared').get();
    const docs = parts.docs.sort((a, b) => Number(a.id) - Number(b.id)).map(part => part.data());
    return {
      geometries: docs.flatMap(d => d.geometries || []),
      groups: docs.flatMap(d => d.groups || []),
      layers: docs.flatMap(d => d.layers || []),
    };
  }

  private tick() {
    this.now.set(Date.now());
    const shown = this.batches().length > 0;
    if (shown && !this.clock) {
      this.clock = setInterval(() => this.zone.run(() => this.tick()), 15000);
    } else if (!shown && this.clock) {
      clearInterval(this.clock);
      this.clock = null;
    }
  }

  private stop() {
    this.stopListening.forEach(stop => stop());
    this.stopListening = [];
    this.projectId = null;
    this.batchDocs.set([]);
    this.jobDocs.set([]);
    if (this.clock) {
      clearInterval(this.clock);
      this.clock = null;
    }
  }

  ngOnDestroy() {
    this.stop();
  }
}
